// src/pages/ExamScheduleManagement.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../api";
import Swal from "sweetalert2";
import { Modal, Button, Form } from "react-bootstrap";

const ExamScheduleManagement = () => {
  const [schedules, setSchedules] = useState([]);
  const [draftRows, setDraftRows] = useState([]);
  const [dirtyIds, setDirtyIds] = useState(new Set());

  const [sessions, setSessions] = useState([]);
  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [terms, setTerms] = useState([]);

  const [filters, setFilters] = useState({
    session_id: "",
    term_id: "",
    exam_id: "",
    class_id: "",
    section_id: "",
  });

  const [formData, setFormData] = useState({
    id: null,
    session_id: "",
    term_id: "",
    exam_id: "",
    class_id: "",
    section_id: "",
    subject_id: "",
    exam_date: "",
    start_time: "",
    end_time: "",
  });

  const [showModal, setShowModal] = useState(false);
  const fileInputRef = useRef(null);

  const examById = useMemo(() => {
    const m = new Map();
    (exams || []).forEach((e) => m.set(String(e.id), e));
    return m;
  }, [exams]);

  useEffect(() => {
    fetchDropdowns();
  }, []);

  useEffect(() => {
    fetchSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchDropdowns = async () => {
    try {
      const [sessionRes, examRes, classRes, sectionRes, subjectRes, termRes] =
        await Promise.all([
          api.get("/sessions"),
          api.get("/exams"),
          api.get("/classes"),
          api.get("/sections"),
          api.get("/subjects"),
          api.get("/terms"),
        ]);

      setSessions(sessionRes.data || []);
      setExams(examRes.data || []);
      setClasses(classRes.data || []);
      setSections(sectionRes.data || []);
      setSubjects(subjectRes.data?.subjects || subjectRes.data || []);
      setTerms(termRes.data || []);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to load dropdowns", "error");
    }
  };

  const fetchSchedules = async () => {
    try {
      const cleanedFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== "" && v !== null && v !== undefined)
      );

      const res = await api.get("/exam-schedules", { params: cleanedFilters });
      const rows = res.data || [];
      setSchedules(rows);

      setDraftRows(
        rows.map((s) => ({
          id: s.id,
          exam_date: s.exam_date || "",
          start_time: s.start_time || "",
          end_time: s.end_time || "",
        }))
      );
      setDirtyIds(new Set());
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to fetch schedules", "error");
    }
  };

  const handleFilterChange = (e) => {
    setFilters((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleGenerateFromScheme = async () => {
    const { session_id, term_id, exam_id, class_id, section_id } = filters;

    if (!session_id || !term_id || !exam_id || !class_id || !section_id) {
      return Swal.fire(
        "Required",
        "Please select Session, Term, Exam, Class, Section first (in Filters).",
        "warning"
      );
    }

    const ex = examById.get(String(exam_id));
    if (ex?.term_id && String(ex.term_id) !== String(term_id)) {
      const c = await Swal.fire({
        title: "Term mismatch",
        text: "Selected Exam seems linked with a different term. Continue anyway?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Continue",
      });
      if (!c.isConfirmed) return;
    }

    try {
      Swal.fire({
        title: "Generating...",
        text: "Creating missing rows from Exam Scheme",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const res = await api.post("/exam-schedules/generate-from-scheme", {
        session_id: Number(session_id),
        term_id: Number(term_id),
        exam_id: Number(exam_id),
        class_id: Number(class_id),
        section_id: Number(section_id),
      });

      const created = res?.data?.created ?? 0;
      const total = res?.data?.totalSubjectsInScheme ?? 0;

      await Swal.fire(
        "Done ✅",
        `Generated successfully.\nCreated: ${created}\nSubjects in Scheme: ${total}\n\nNow fill Date/Start/End in table and click "Save All".`,
        "success"
      );

      fetchSchedules();
    } catch (e) {
      console.error(e);
      Swal.fire(
        "Error",
        e?.response?.data?.message || "Failed to generate from scheme",
        "error"
      );
    }
  };

  const markDirty = (id) => {
    setDirtyIds((prev) => {
      const next = new Set(prev);
      next.add(String(id));
      return next;
    });
  };

  const updateDraftCell = (rowIndex, key, value) => {
    setDraftRows((prev) => {
      const next = [...prev];
      next[rowIndex] = { ...next[rowIndex], [key]: value };
      return next;
    });
    const sid = schedules[rowIndex]?.id;
    if (sid) markDirty(sid);
  };

  const handleSaveAllDateTimes = async () => {
    const updates = draftRows
      .filter((r) => dirtyIds.has(String(r.id)))
      .map((r) => ({
        id: r.id,
        exam_date: r.exam_date || null,
        start_time: r.start_time || null,
        end_time: r.end_time || null,
      }));

    if (!updates.length) {
      return Swal.fire("No Changes", "Nothing to save.", "info");
    }

    const bad = updates.find((u) => !u.exam_date || !u.start_time || !u.end_time);
    if (bad) {
      return Swal.fire(
        "Validation",
        "Please fill Date + Start + End for all edited rows (cannot save partial).",
        "warning"
      );
    }

    try {
      Swal.fire({
        title: "Saving...",
        text: "Updating Date/Start/End",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const res = await api.put("/exam-schedules/bulk-datetime", { updates });
      const updated = res?.data?.updated ?? 0;

      await Swal.fire("Saved ✅", `${updated} row(s) updated successfully.`, "success");
      fetchSchedules();
    } catch (e) {
      console.error(e);
      Swal.fire(
        "Error",
        e?.response?.data?.message || "Failed to save",
        "error"
      );
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const closeModal = () => setShowModal(false);

  const openAddModal = () => {
    setFormData({
      id: null,
      session_id: filters.session_id || "",
      term_id: filters.term_id || "",
      exam_id: filters.exam_id || "",
      class_id: filters.class_id || "",
      section_id: filters.section_id || "",
      subject_id: "",
      exam_date: "",
      start_time: "",
      end_time: "",
    });
    setShowModal(true);
  };

  const handleEdit = (schedule) => {
    setFormData({
      id: schedule.id,
      session_id: schedule.session_id || schedule.session?.id || "",
      term_id: schedule.term_id || schedule.term?.id || "",
      exam_id: schedule.exam_id || schedule.exam?.id || "",
      class_id: schedule.class_id || schedule.class?.id || "",
      section_id: schedule.section_id || schedule.section?.id || "",
      subject_id: schedule.subject_id || schedule.subject?.id || "",
      exam_date: schedule.exam_date || "",
      start_time: schedule.start_time || "",
      end_time: schedule.end_time || "",
    });
    setShowModal(true);
  };

  const handleDuplicate = (schedule) => {
    setFormData({
      id: null,
      session_id: schedule.session_id || schedule.session?.id || "",
      term_id: schedule.term_id || schedule.term?.id || "",
      exam_id: schedule.exam_id || schedule.exam?.id || "",
      class_id: schedule.class_id || schedule.class?.id || "",
      section_id: schedule.section_id || schedule.section?.id || "",
      subject_id: schedule.subject_id || schedule.subject?.id || "",
      exam_date: schedule.exam_date || "",
      start_time: schedule.start_time || "",
      end_time: schedule.end_time || "",
    });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    const {
      id,
      session_id,
      term_id,
      exam_id,
      class_id,
      section_id,
      subject_id,
      exam_date,
      start_time,
      end_time,
    } = formData;

    if (
      !session_id ||
      !term_id ||
      !exam_id ||
      !class_id ||
      !section_id ||
      !subject_id ||
      !exam_date ||
      !start_time ||
      !end_time
    ) {
      return Swal.fire(
        "Validation Error",
        "Please fill all required fields",
        "warning"
      );
    }

    const payload = {
      ...formData,
      session_id: Number(session_id),
      term_id: Number(term_id),
      exam_id: Number(exam_id),
      class_id: Number(class_id),
      section_id: Number(section_id),
      subject_id: Number(subject_id),
    };

    try {
      if (id) {
        await api.put(`/exam-schedules/${id}`, payload);
        Swal.fire("Updated", "Schedule updated successfully", "success");
      } else {
        await api.post("/exam-schedules", payload);
        Swal.fire("Success", "Schedule created successfully", "success");
      }
      closeModal();
      fetchSchedules();
    } catch (err) {
      console.error(err);
      Swal.fire("Error", err?.response?.data?.error || "Failed to save schedule", "error");
    }
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "This will permanently delete the schedule.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });

    if (confirm.isConfirmed) {
      try {
        await api.delete(`/exam-schedules/${id}`);
        Swal.fire("Deleted", "Schedule deleted.", "success");
        fetchSchedules();
      } catch (err) {
        console.error(err);
        Swal.fire("Error", "Failed to delete schedule", "error");
      }
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get("/exam-schedules/export", {
        params: filters,
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "ExamSchedules.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Export failed:", error);
      Swal.fire("Error", "Failed to export Excel", "error");
    }
  };

  const handleImportClick = () => fileInputRef.current?.click();

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const form = new FormData();
    form.append("file", file);

    if (filters.session_id) form.append("session_id", filters.session_id);
    if (filters.term_id) form.append("term_id", filters.term_id);
    if (filters.exam_id) form.append("exam_id", filters.exam_id);
    if (filters.class_id) form.append("class_id", filters.class_id);
    if (filters.section_id) form.append("section_id", filters.section_id);

    try {
      await api.post("/exam-schedules/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      Swal.fire("Success", "Import completed", "success");
      fetchSchedules();
    } catch (err) {
      console.error(err);
      Swal.fire(
        "Error",
        err?.response?.data?.message || "Failed to import file",
        "error"
      );
    } finally {
      e.target.value = "";
    }
  };

  const canGenerate =
    !!filters.session_id &&
    !!filters.term_id &&
    !!filters.exam_id &&
    !!filters.class_id &&
    !!filters.section_id;

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        <h2 className="m-0">📘 Exam Schedule Management</h2>

        <div className="d-flex gap-2 flex-wrap">
          <Button
            variant="outline-info"
            onClick={handleGenerateFromScheme}
            disabled={!canGenerate}
            title={
              canGenerate
                ? "Create missing schedule rows from Exam Scheme"
                : "Select Session, Term, Exam, Class, Section first"
            }
          >
            ⚡ Generate from Scheme
          </Button>

          <Button
            variant="success"
            onClick={handleSaveAllDateTimes}
            disabled={!dirtyIds.size}
            title={dirtyIds.size ? "Save all changed date/time rows" : "No changes"}
          >
            💾 Save All Dates/Times {dirtyIds.size ? `(${dirtyIds.size})` : ""}
          </Button>
        </div>
      </div>

      <div className="card mt-4 mb-4">
        <div className="card-body">
          <h5 className="card-title">Filter</h5>
          <div className="row g-2">
            <div className="col-md-3">
              <label>Session</label>
              <Form.Select
                name="session_id"
                value={filters.session_id}
                onChange={handleFilterChange}
              >
                <option value="">All Sessions</option>
                {sessions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Form.Select>
            </div>

            <div className="col-md-3">
              <label>Term</label>
              <Form.Select
                name="term_id"
                value={filters.term_id}
                onChange={handleFilterChange}
              >
                <option value="">All Terms</option>
                {terms.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Form.Select>
            </div>

            <div className="col-md-3">
              <label>Exam</label>
              <Form.Select
                name="exam_id"
                value={filters.exam_id}
                onChange={handleFilterChange}
              >
                <option value="">All Exams</option>
                {exams.map((ex) => (
                  <option key={ex.id} value={ex.id}>
                    {ex.name}
                  </option>
                ))}
              </Form.Select>
            </div>

            <div className="col-md-3">
              <label>Class</label>
              <Form.Select
                name="class_id"
                value={filters.class_id}
                onChange={handleFilterChange}
              >
                <option value="">All Classes</option>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.class_name}
                  </option>
                ))}
              </Form.Select>
            </div>

            <div className="col-md-3">
              <label>Section</label>
              <Form.Select
                name="section_id"
                value={filters.section_id}
                onChange={handleFilterChange}
              >
                <option value="">All Sections</option>
                {sections.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.section_name}
                  </option>
                ))}
              </Form.Select>
            </div>

            <div className="col-12 d-flex justify-content-between align-items-center mt-2">
              <div className="text-muted">
                Tip: Session + Filter select karo → <b>Generate from Scheme</b> → table me
                dates/times fill karke <b>Save All</b>.
              </div>

              <div className="d-flex gap-2">
                <Button variant="primary" onClick={openAddModal} title="Manual Add (optional)">
                  ➕ Add Schedule
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-between mb-3 flex-wrap gap-2">
        <div className="d-flex gap-2 flex-wrap">
          <Button variant="outline-success" onClick={handleExport}>
            ⬇️ Export Excel
          </Button>
          <Button variant="outline-primary" onClick={handleImportClick}>
            ⬆️ Import Excel
          </Button>

          <Form.Control
            type="file"
            accept=".xlsx"
            ref={fileInputRef}
            onChange={handleImport}
            style={{ display: "none" }}
          />
        </div>

        <div className="text-muted">
          Rows: <b>{schedules.length}</b> {dirtyIds.size ? ` | Edited: ${dirtyIds.size}` : ""}
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <h5 className="card-title">Scheduled Exams</h5>

          {schedules.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-bordered table-striped align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 50 }}>#</th>
                    <th>Session</th>
                    <th>Term</th>
                    <th>Exam</th>
                    <th>Class</th>
                    <th>Section</th>
                    <th>Subject</th>
                    <th style={{ width: 160 }}>Date</th>
                    <th style={{ width: 130 }}>Start</th>
                    <th style={{ width: 130 }}>End</th>
                    <th style={{ width: 190 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {schedules.map((s, i) => {
                    const d = draftRows[i] || { exam_date: "", start_time: "", end_time: "" };
                    const isDirty = dirtyIds.has(String(s.id));

                    return (
                      <tr key={s.id} className={isDirty ? "table-warning" : ""}>
                        <td>{i + 1}</td>
                        <td>{s.session?.name || s.session_name || s.session_id || "-"}</td>
                        <td>{s.term?.name || "-"}</td>
                        <td>{s.exam?.name || "-"}</td>
                        <td>{s.class?.class_name || "-"}</td>
                        <td>{s.section?.section_name || "-"}</td>
                        <td>{s.subject?.name || "-"}</td>

                        <td>
                          <Form.Control
                            type="date"
                            value={d.exam_date}
                            onChange={(e) => updateDraftCell(i, "exam_date", e.target.value)}
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="time"
                            value={d.start_time}
                            onChange={(e) => updateDraftCell(i, "start_time", e.target.value)}
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="time"
                            value={d.end_time}
                            onChange={(e) => updateDraftCell(i, "end_time", e.target.value)}
                          />
                        </td>

                        <td>
                          <Button
                            variant="outline-info"
                            size="sm"
                            className="me-2"
                            onClick={() => handleDuplicate(s)}
                            title="Duplicate Schedule"
                          >
                            📄
                          </Button>

                          <Button
                            variant="warning"
                            size="sm"
                            className="me-2"
                            onClick={() => handleEdit(s)}
                          >
                            Edit
                          </Button>

                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDelete(s.id)}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted m-0">No schedules found.</p>
          )}
        </div>
      </div>

      <Modal show={showModal} onHide={closeModal} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>
            {formData.id ? "✏️ Edit Schedule" : "➕ Add / Duplicate Schedule"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ paddingBottom: "0.5rem" }}>
          <Form>
            <div className="row g-2">
              <div className="col-12 col-md-6 col-lg-4">
                <Form.Group className="mb-2">
                  <Form.Label>Session</Form.Label>
                  <Form.Select
                    name="session_id"
                    value={formData.session_id}
                    onChange={handleFormChange}
                    disabled={!!formData.id}
                  >
                    <option value="">Select Session</option>
                    {sessions.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>

              <div className="col-12 col-md-6 col-lg-4">
                <Form.Group className="mb-2">
                  <Form.Label>Term</Form.Label>
                  <Form.Select
                    name="term_id"
                    value={formData.term_id}
                    onChange={handleFormChange}
                    disabled={!!formData.id}
                  >
                    <option value="">Select Term</option>
                    {terms.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>

              <div className="col-12 col-md-6 col-lg-4">
                <Form.Group className="mb-2">
                  <Form.Label>Exam</Form.Label>
                  <Form.Select
                    name="exam_id"
                    value={formData.exam_id}
                    onChange={handleFormChange}
                    disabled={!!formData.id}
                  >
                    <option value="">Select Exam</option>
                    {exams.map((ex) => (
                      <option key={ex.id} value={ex.id}>
                        {ex.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>

              <div className="col-12 col-md-6 col-lg-4">
                <Form.Group className="mb-2">
                  <Form.Label>Class</Form.Label>
                  <Form.Select
                    name="class_id"
                    value={formData.class_id}
                    onChange={handleFormChange}
                    disabled={!!formData.id}
                  >
                    <option value="">Select Class</option>
                    {classes.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.class_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>

              <div className="col-12 col-md-6 col-lg-4">
                <Form.Group className="mb-2">
                  <Form.Label>Section</Form.Label>
                  <Form.Select
                    name="section_id"
                    value={formData.section_id}
                    onChange={handleFormChange}
                    disabled={!!formData.id}
                  >
                    <option value="">Select Section</option>
                    {sections.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.section_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>

              <div className="col-12 col-md-6 col-lg-4">
                <Form.Group className="mb-2">
                  <Form.Label>Subject</Form.Label>
                  <Form.Select
                    name="subject_id"
                    value={formData.subject_id}
                    onChange={handleFormChange}
                    disabled={!!formData.id}
                  >
                    <option value="">Select Subject</option>
                    {subjects.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>

              <div className="col-12 col-md-6 col-lg-4">
                <Form.Group className="mb-2">
                  <Form.Label>Exam Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="exam_date"
                    value={formData.exam_date}
                    onChange={handleFormChange}
                  />
                </Form.Group>
              </div>

              <div className="col-12 col-md-6 col-lg-4">
                <Form.Group className="mb-2">
                  <Form.Label>Start Time</Form.Label>
                  <Form.Control
                    type="time"
                    name="start_time"
                    value={formData.start_time}
                    onChange={handleFormChange}
                  />
                </Form.Group>
              </div>

              <div className="col-12 col-md-6 col-lg-4">
                <Form.Group className="mb-2">
                  <Form.Label>End Time</Form.Label>
                  <Form.Control
                    type="time"
                    name="end_time"
                    value={formData.end_time}
                    onChange={handleFormChange}
                  />
                </Form.Group>
              </div>
            </div>

            <div className="mt-2 text-muted">
              Note: In auto mode, schedules are created from Scheme; you only update date/time.
            </div>
          </Form>
        </Modal.Body>

        <Modal.Footer style={{ paddingTop: "0.25rem" }}>
          <Button variant="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            {formData.id ? "Update" : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ExamScheduleManagement;